<script setup lang="ts">
import { BuildingOfficeIcon, UserCircleIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import type { GlassTabList } from '@/tabs/types.ts'
import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier'
import type { RedirectRoute } from '@/utilities/routers'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import AlertMessage from '@/components/common/ui/overlays/AlertMessage.vue'
import SlideInContainer from '@/components/common/ui/overlays/SlideInContainer.vue'
import GlassTab from '@/components/common/ui/tabs/GlassTab.vue'
import CreateOrganizationForm from '@/components/createRelationGroup/CreateOrganizationForm.vue'
import CreatePersonForm from '@/components/createRelationGroup/CreatePersonForm.vue'
import { useCreateRelationPermissions } from '@/composables/useCreateRelationPermissions'
import { RelationGroupType, RelationType } from '@/constants/relations'
import { AlertType } from '@/constants/ui'
import { routePersonDetail } from '@/routers/routes/mainRoutes/routeRelationships/personsRoutes'
import { getOrganizationDetailRoute } from '@/routers/routes/mainRoutes/routeRelationships/routeOrganizationDetail'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { translationFunctionDefault } from '@/utilities/translations'
import { validateStringAgainstEnum } from '@/utilities/typeguards'

const router = useRouter()
const route = useRoute()

const tfDefault = translationFunctionDefault()

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { createRelationOverlayState } = storeToRefs(createRelationSlideInStore)
const { closeCreateRelationOverlay } = createRelationSlideInStore
const { canCreateOrganizationRelation, canCreatePersonRelation } = useCreateRelationPermissions({
  getOrganizationRelationType: () => createRelationSlideInStore.organisationRelationType,
})

// Confirmation modal state and handlers
const confirmDiscardOpen = ref(false)
const isFormDirty = ref(false)
const setFormDirty = (value: boolean): void => {
  isFormDirty.value = value
}
const requestClose = (event?: MouseEvent): void => {
  if (event?.target instanceof HTMLElement && event.target.closest('.alert-message-overlay')) {
    return
  }
  if (isFormDirty.value) {
    confirmDiscardOpen.value = true
  } else {
    closeCreateRelationOverlay()
  }
}
const onStay = (): void => {
  confirmDiscardOpen.value = false
}
const onLeave = async (): Promise<void> => {
  confirmDiscardOpen.value = false
  await nextTick()
  closeCreateRelationOverlay()
}

const getRedirectPath = (doc: DocIdentifier): null | RedirectRoute => {
  if (doc.doctype === 'Contact') {
    return { name: routePersonDetail.name, params: { id: doc.name } }
  } else if (validateStringAgainstEnum(RelationType, doc.doctype) !== undefined) {
    return { name: getOrganizationDetailRoute(doc.doctype).name, params: { id: doc.name } }
  }

  return null
}

const onRelationCreated = async (doc: DocIdentifier): Promise<void> => {
  if (!doc.doctype) {
    return
  }

  const cameFromPerson = createRelationSlideInStore.prefillMetaData?.sourceEntityType === 'person'

  closeCreateRelationOverlay()

  if (cameFromPerson) {
    createRelationSlideInStore.initialPersonOrganisationRelationLinkName = doc.name
  }

  if (!cameFromPerson && route.path.startsWith('/relationships')) {
    const path = getRedirectPath(doc)
    if (path !== null) {
      await router.push(path)
    }
  }
}
const tabList: GlassTabList = [
  {
    icon: BuildingOfficeIcon,
    key: RelationGroupType.organizations,
    name: tfDefault('createRelation.tabOrganizationsTitle'),
    testid: TEST_IDS_CREATE.createRelation.createOrganizationTab,
  },
  {
    icon: UserCircleIcon,
    key: RelationGroupType.persons,
    name: tfDefault('createRelation.tabPersonsTitle'),
    testid: TEST_IDS_CREATE.createRelation.createPersonTab,
  },
]
</script>

<template>
  <div
    v-if="createRelationOverlayState.open"
    class="fixed inset-0 z-[10000] flex w-screen items-center justify-center bg-black-950 bg-opacity-20"
  >
    <SlideInContainer
      v-click-outside="requestClose"
      :confirm-before-close="isFormDirty"
      :title="tfDefault('createRelation.createPanelTitle')"
      @close="requestClose"
      @request-close="requestClose"
    >
      <div class="flex h-full flex-col">
        <div class="px-8">
          <GlassTab
            v-model:selected-tab-key="createRelationSlideInStore.createRelationOverlayState.type"
            class="my-5"
            :tab-list="tabList"
            :wide="false"
          />
        </div>

        <CreateOrganizationForm
          v-if="
            createRelationOverlayState.type === RelationGroupType.organizations &&
            canCreateOrganizationRelation
          "
          :on-created="onRelationCreated"
          @dirty-change="setFormDirty"
        />
        <CreatePersonForm
          v-else-if="
            createRelationOverlayState.type === RelationGroupType.persons && canCreatePersonRelation
          "
          :on-created="onRelationCreated"
          @dirty-change="setFormDirty"
        />
        <div v-else class="flex h-full flex-1 flex-col justify-between overflow-hidden">
          <p class="pl-8">{{ tfDefault('createRelation.noPermissionToAccessTabCreation') }}</p>
        </div>
      </div>
    </SlideInContainer>

    <AlertMessage
      :alert-type="AlertType.info"
      :cancel-label="tfDefault('createRelation.discardChangesLeaveCta')"
      :cta-label="tfDefault('createRelation.discardChangesStayCta')"
      :message="tfDefault('createRelation.discardChangesMessage')"
      :message-title="tfDefault('createRelation.discardChangesTitle')"
      :open="confirmDiscardOpen"
      @on-close="onLeave"
      @on-cta-click="onStay"
    />
  </div>
</template>
